/* eslint-disable no-console */
import React from 'react';
import HBTTableCell from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementTable/components/HBTTableCell';
import formatWithDollar from 'Feature/V2/HbtPortfolioManagement/utils/FormatAmountWithDollar';
import { formatRatio } from 'Feature/V2/HbtPortfolioManagement/utils/FormatRatio';
import { occupancyMapping } from 'Feature/V2/HbtPortfolioManagement/utils/OccupancyMapping';
import { MemoizedHbtDataTableCell } from 'Feature/V2/Shared/MemoizedHbtDataTableCell';
import { TFunction } from 'i18next';
import { MRT_Cell, MRT_Row } from 'material-react-table';
import { requestTypeMapping } from '../../../utils/RequestTypeMapping';
import { InsuredApprovedLenderName, MasterPoolTableFields } from '../../HbtMasterPool/types';
import LoanStatusPill from '../../../shared/LoanStatusPill';
import { TranslationType } from '../types';
import { loanStatusMapping } from '../utils/LoanStatusMapping';
import { masterPoolTypeMapping } from '../utils/MasterPoolTypeMapping';
import i18n from 'i18next';
import formatDigits from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/PortfolioCard/utils/FormatDigits';
import { CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import { Stack, Tooltip } from '@mui/material';
import styles from './style.module.scss';

const isItValid = (value: any) => {
  return value != 'undefined' && value !== 'null' && value !== null && value != undefined;
};

type MasterPoolRequestTypeKey = keyof ReturnType<typeof requestTypeMapping>;

const getOccupancyTypeLabel = (
  occupancyType: keyof typeof occupancyMapping,
  t: TranslationType
) => {
  return occupancyMapping(t)[occupancyType];
};

export const getRequestTypeLabel = (
  requestType: MasterPoolRequestTypeKey | string | undefined,
  t: TranslationType
): string => {
  const mapping = requestTypeMapping(t);
  return mapping[requestType as MasterPoolRequestTypeKey] ?? requestType ?? '';
};

const getInsuredApprovedlenderName = (data: InsuredApprovedLenderName, language: string) => {
  return language === 'en' ? data.englishName : data.frenchName;
};

const getTermInsuranceLabel = (data: number, t: TFunction<'HbtMasterPool', undefined>) => {
  const termInsurance = data ? data.toString() : '';
  const termValue = termInsurance.trim() === '' ? 0 : Number(termInsurance);
  let suffix = '';
  if (termInsurance === '' || termValue === 0) {
    suffix = '';
  } else if (termValue === 1) {
    suffix = t('year');
  } else {
    suffix = t('years');
  }
  return termInsurance + (suffix ? ` ${suffix}` : '');
};

export const createInsuredLenderNameRenderer =
  (language: string) =>
  ({ cell }: { cell: any }) => {
    const data = cell.getValue();
    const displayName = getInsuredApprovedlenderName(data, language);
    return (
      <MemoizedHbtDataTableCell
        dataValue={data?.englishName || ''}
        cellContent={<HBTTableCell title={displayName}>{displayName}</HBTTableCell>}
      />
    );
  };

export const createOccupancyTypeRenderer =
  (t: TFunction) =>
  ({ cell }: { cell: any }) => {
    const value = cell.getValue();
    const label = getOccupancyTypeLabel(value as keyof typeof occupancyMapping, t);
    return (
      <MemoizedHbtDataTableCell
        dataValue={value as string}
        cellContent={<HBTTableCell title={label}>{label}</HBTTableCell>}
      />
    );
  };

export const createTermInsuranceRenderer =
  (t: TFunction) =>
  ({ cell }: { cell: any }) => {
    const value = cell.getValue() ?? '';
    const label = getTermInsuranceLabel(+value, t);
    return (
      <MemoizedHbtDataTableCell
        dataValue={String(value)}
        cellContent={<HBTTableCell title={label}>{label}</HBTTableCell>}
      />
    );
  };

export const createRequestTypeRenderer =
  (t: TFunction) =>
  ({ cell }: { cell: MRT_Cell<MasterPoolTableFields, unknown> }) => {
    const value = cell.getValue();
    const label = getRequestTypeLabel(value as keyof typeof requestTypeMapping, t);
    return <HBTTableCell title={label}>{label}</HBTTableCell>;
  };

export const createAmountSubmittedRenderer =
  (language: string) =>
  ({ cell }: { cell: any }) => {
    const amount = cell.getValue();
    const formattedAmount = formatWithDollar(amount, language);
    return (
      <MemoizedHbtDataTableCell
        dataValue={formattedAmount}
        cellContent={<HBTTableCell title={formattedAmount}>{formattedAmount}</HBTTableCell>}
      />
    );
  };

export const renderRequestReceiptDateCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue() ?? '';

  return (
    <MemoizedHbtDataTableCell
      dataValue={String(value)}
      cellContent={<HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>}
    />
  );
};

export const renderFraudDetectionDateCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue();
  return (
    <>
      {value != null ? (
        <MemoizedHbtDataTableCell
          dataValue={String(value)}
          cellContent={<HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>}
        />
      ) : (
        <CmhcIcon type="icon_indeterminate" size={CmhcIconSize.MEDIUM} />
      )}
    </>
  );
};

export const renderMasterPoolCell =
  (t: TFunction) =>
  ({ cell, row }: { cell: any; row: MRT_Row<MasterPoolTableFields> }) => {
    const value = cell.getValue();
    return (
      <Stack direction="row" gap={2} justifyContent="center" alignItems="center">
        <HBTTableCell title={value}>{value}</HBTTableCell>
        {Boolean(Number(row.original.clockIcon)) ? (
          <Tooltip
            title={t('Tooltip-For-Currency-Masterpool')}
            placement="bottom"
            arrow
            slotProps={{
              tooltip: {
                className: styles.requestOverviewDetailsToolTip
              },
              arrow: {
                className: styles.tooltipArrow
              }
            }}
            className={styles.toolTipIcon}
          >
            <div>
              <CmhcIcon size={CmhcIconSize.SMALL} type="icon_overdue" />
            </div>
          </Tooltip>
        ) : Boolean(Number(row.original.balanceIcon)) ? (
          <Tooltip
            title={t('Tooltip-For-Unbalanced-Masterpool')}
            placement="bottom"
            arrow
            slotProps={{
              tooltip: {
                className: styles.requestOverviewDetailsToolTip
              },
              arrow: {
                className: styles.tooltipArrow
              }
            }}
          >
            <div>
              <CmhcIcon size={CmhcIconSize.SMALL} type="icon_unbalanced" />
            </div>
          </Tooltip>
        ) : (
          ''
        )}
      </Stack>
    );
  };

const getLoanStatusLabel = (status: keyof typeof loanStatusMapping, t: TranslationType) => {
  return loanStatusMapping(t)[status];
};

const getPoolTypeLabel = (status: keyof typeof masterPoolTypeMapping, t: TranslationType) => {
  return masterPoolTypeMapping(t)[status];
};

export const createStatusRenderer =
  (t: TFunction) =>
  ({ cell }: { cell: any }) => {
    const value = cell.getValue();
    return (
      <MemoizedHbtDataTableCell
        dataValue={value as string}
        cellContent={
          <HBTTableCell title={getLoanStatusLabel(value as keyof typeof loanStatusMapping, t)}>
            <LoanStatusPill
              status={value}
              label={getLoanStatusLabel(value as keyof typeof loanStatusMapping, t)}
            />
          </HBTTableCell>
        }
      />
    );
  };

export const renderInsuredLenderCodeCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue();
  return (
    <MemoizedHbtDataTableCell
      dataValue={value as string}
      cellContent={<HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>}
    />
  );
};
export const renderTotalLoansSubmitted = ({ cell }: { cell: any }) => {
  const value = cell.getValue() ?? '';
  const displayValue = formatDigits(value, i18n.language);
  return (
    <MemoizedHbtDataTableCell
      dataValue={String(value)}
      cellContent={<HBTTableCell title={displayValue}>{displayValue}</HBTTableCell>}
    />
  );
};
export const createRenderTotalAmountSubmitted =
  (language: string) =>
  ({ cell }: { cell: any }) => {
    const value = String(cell.getValue()) ?? '';
    return (
      <MemoizedHbtDataTableCell
        dataValue={formatWithDollar(Number(value?.replace(/,/g, '')), language)}
        cellContent={
          <HBTTableCell title={value}>
            {formatWithDollar(Number(value?.replace(/,/g, '')), language)}
          </HBTTableCell>
        }
      />
    );
  };

export const renderNumberOfLoansCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue();
  const displayValue = String(value);
  return (
    <MemoizedHbtDataTableCell
      dataValue={String(value)}
      cellContent={<HBTTableCell title={displayValue}>{displayValue}</HBTTableCell>}
    />
  );
};

export const renderOccupanyType = ({ cell }: { cell: any }) => {
  const value = cell.getValue();
  const displayValue = String(value);
  return (
    <MemoizedHbtDataTableCell
      dataValue={String(value)}
      cellContent={<HBTTableCell title={displayValue}>{displayValue}</HBTTableCell>}
    />
  );
};

export const createRenderRateWithPercentage =
  (lang: string, formatingDecimals: number) =>
  ({ cell }: { cell: any }) => {
    const value = cell.getValue();
    const displayValue = isItValid(value)
      ? formatRatio(Number(value), lang, formatingDecimals)
      : value;
    return (
      <>
        {isItValid(displayValue) ? (
          <MemoizedHbtDataTableCell
            dataValue={displayValue}
            cellContent={<HBTTableCell title={displayValue}>{displayValue}</HBTTableCell>}
          />
        ) : (
          <CmhcIcon type="icon_indeterminate" size={CmhcIconSize.MEDIUM} />
        )}
      </>
    );
  };

export const createRenderRate =
  (lang: string) =>
  ({ cell }: { cell: any }) => {
    const value = cell.getValue();
    const displayValue = String(value).includes(',')
      ? formatRatio(Number(value.replace(/,/g, '')), lang)
      : String(value);
    return (
      <>
        {displayValue != 'undefined' && displayValue !== 'null' ? (
          <MemoizedHbtDataTableCell
            dataValue={displayValue}
            cellContent={<HBTTableCell title={displayValue}>{displayValue}</HBTTableCell>}
          />
        ) : (
          <CmhcIcon type="icon_indeterminate" size={CmhcIconSize.MEDIUM} />
        )}
      </>
    );
  };
export const renderString = ({ cell }: { cell: any }) => {
  const value = cell.getValue();
  return (
    <MemoizedHbtDataTableCell
      dataValue={value}
      cellContent={<HBTTableCell title={value}>{value}</HBTTableCell>}
    />
  );
};

export const createPoolTypeRenderer =
  (t: TFunction) =>
  ({ cell }: { cell: any }) => {
    let value = cell.getValue();
    value = getPoolTypeLabel(value as keyof typeof masterPoolTypeMapping, t);
    return (
      <MemoizedHbtDataTableCell
        dataValue={value as string}
        cellContent={<HBTTableCell title={value}>{value}</HBTTableCell>}
      />
    );
  };

export const handleHistoricalRemarks =
  (
    t: TFunction,
    setSelectedMasterPoolForRemark: React.Dispatch<React.SetStateAction<(number | null)[]>>,
    toggleHistoricalRemarksDrawer: (newOpen: boolean) => void
  ) =>
  ({ cell, row }: { cell: any; row: any }) => {
    const remarkPresent = cell.getValue();
    const { masterPoolCmhcAccountNumber } = row.original;

    const handleRemarksIcon = (event: React.MouseEvent<SVGSVGElement>) => {
      event.stopPropagation();
      setSelectedMasterPoolForRemark([+masterPoolCmhcAccountNumber]);
      toggleHistoricalRemarksDrawer(true);
    };
    return remarkPresent ? (
      <CmhcIcon
        size={CmhcIconSize.SMALL}
        type="icon_comment"
        onClick={handleRemarksIcon}
        style={{ cursor: 'pointer' }}
      />
    ) : (
      ''
    );
  };
